import { FC, useEffect, useState } from 'react';

import './Discounts.css';
import { Card, CardTitle, EmptyState, Meta, Notice, SectionHeader, StatusBadge } from '../../ui';
import type { Preview } from '../group/TillPreview';
import BundleOfferCard from './bundle/BundleOfferCard';
import BundleOfferEditor from './bundle/BundleOfferEditor';
import { BundleFormState, adminGroupLabel, draftFromBundle, emptyGroup } from './bundle/types';
import { useBundles } from './bundle/useBundles';

type DiscountsProps = {
  /** Publish the bundle being edited so the page can preview it. */
  onPreview?: (preview: Preview | null) => void;
  /** Called when the session is no longer valid, so App can drop the session. */
  onUnauthorized: () => void;
};

/**
 * Discount bundles.
 *
 * Always embedded by CatalogPage under GroupPage, which owns the page's heading,
 * padding and scroll shell. This page owns the bundle editor, the compact
 * existing-bundle list and the delete confirmation.
 */
const Discounts: FC<DiscountsProps> = ({ onUnauthorized, onPreview }) => {
  const { categories, tags, bundles, items, loading, error, notice, busyKey, createNew, saveExisting, remove, dismissError } =
    useBundles(onUnauthorized);
  /** Bumped after a create so the new-bundle editor remounts empty. */
  const [createKey, setCreateKey] = useState(0);
  /** The one existing bundle currently expanded for editing, if any. */
  const [editingId, setEditingId] = useState<number | null>(null);

  // Publish whichever bundle is open so the page can preview how it reads on the
  // till. Derived from state rather than hooked into each handler, so it stays
  // right when a save changes the price.
  useEffect(() => {
    // The edited bundle, or the first one, so the rail always shows something.
    const open = bundles.find((bundle) => bundle.id === editingId) ?? bundles[0];
    onPreview?.(
      open
        ? {
            kind: 'bundle',
            name: open.name,
            priceCents: open.price_cents,
            regularMinCents: open.components_total_cents,
            regularMaxCents: open.components_max_cents,
            groups: open.groups.map((group) => ({ quantity: group.quantity, label: adminGroupLabel(group) })),
          }
        : null
    );
  }, [bundles, editingId, onPreview]);

  const activeCount = bundles.filter((bundle) => bundle.active).length;

  const handleCreate = (form: BundleFormState) => {
    createNew(form).then((created) => {
      if (created) setCreateKey((key) => key + 1);
    });
  };

  const handleSave = (bundleId: number, form: BundleFormState) => {
    saveExisting(bundleId, form).then((updated) => {
      if (updated) setEditingId(null);
    });
  };

  const handleDelete = (bundleId: number) => {
    remove(bundleId).then((deleted) => {
      if (deleted) setEditingId(null);
    });
  };

  return (
    <div className="discounts">
      <SectionHeader
        title="Bundles"
        description={
          bundles.length +
          ' bundle' +
          (bundles.length === 1 ? '' : 's') +
          ' · ' +
          activeCount +
          ' active · ' +
          items.length +
          ' items available'
        }
      />

      {error && (
        <Notice variant="error" onDismiss={dismissError}>
          {error}
        </Notice>
      )}
      {notice && <Notice variant="success">{notice}</Notice>}

      <section className="discountNew">
        <CardTitle>New bundle</CardTitle>
        <Meta>
          Sell several items together for one price. Group a category, a tag or exact items, and the till applies the discount on its own.
        </Meta>
        <Card className="discountCard">
          <BundleOfferEditor
            key={createKey}
            initial={{ name: '', price: '', active: true, groups: [emptyGroup(categories)] }}
            categories={categories}
            items={items}
            tags={tags}
            submitLabel="Create bundle"
            busy={busyKey === 'new'}
            onSubmit={handleCreate}
          />
        </Card>
      </section>

      <section className="discountExisting">
        <CardTitle>Existing bundles</CardTitle>
        {loading ? (
          <Meta role="status">Loading…</Meta>
        ) : bundles.length === 0 ? (
          <EmptyState title="No bundles yet" description="Create your first bundle above." />
        ) : (
          bundles.map((bundle) =>
            editingId === bundle.id ? (
              <Card className="discountCard discountEditing" key={bundle.id} data-bundle-name={bundle.name}>
                <div className="discountEditingHead">
                  <span className="discountBundleName" title={bundle.name}>
                    {bundle.name}
                  </span>
                  <StatusBadge tone={bundle.active ? 'success' : 'neutral'}>{bundle.active ? 'Active' : 'Inactive'}</StatusBadge>
                </div>
                <BundleOfferEditor
                  initial={draftFromBundle(bundle)}
                  categories={categories}
                  items={items}
                  tags={tags}
                  submitLabel="Save changes"
                  busy={busyKey === String(bundle.id)}
                  onSubmit={(form) => handleSave(bundle.id, form)}
                  onCancel={() => setEditingId(null)}
                  onDelete={() => handleDelete(bundle.id)}
                />
              </Card>
            ) : (
              <BundleOfferCard key={bundle.id} bundle={bundle} onEdit={() => setEditingId(bundle.id)} />
            )
          )
        )}
      </section>
    </div>
  );
};

export default Discounts;
